'use client';

import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    ButtonLink,
    Icon,
    InputSelect,
    NumberInput,
    Tooltip,
} from '~/components/primitives';
import {
    VariantLanguageInlineSelect,
    useVariantOptions,
    usePriceInfo,
} from '../VariantLanguageInlineSelect';
import { useI18n } from '~/features/i18n';
import useWishlistState, { type WishlistItem } from '~/features/wishlist/useWishlistState';
import useCartState from '../useCartState';
import { api } from '~/modules';
import { graphql } from '@funkey/types/graphql/gql';
import { usePostHog } from 'posthog-js/react';
import {
    usePortalCtx,
    usePortalHREF_relative,
    usePortalHREFs,
    usePortalRouter,
} from '~/features/global/portal';
import { Units } from '~/modules/units';
import { VATKindDisplay } from '~/features/portals/money';
import { useTRPC } from '~/modules/trpc/client';
import { useQuery } from '@tanstack/react-query';
import {
    CACHED_PARTICIPANTS_FILTER,
    MAX_PARTICIPANTS,
} from '~/features/activities/search/client/context';
import { ActivityListCardTitle } from '../ui/activity-list-card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '~/components/ui/dialog';
import {
    Wishlist_ShareLinkDialog_Mount,
    useWishlist_ShareLinkDialog_OpenAction,
} from '~/features/wishlist/ShareWishlistDialog';
import { ActivityItemCard } from '../ui/activity-item-card';
import { Badge } from '~/components/ui/badge';
import { useLocalStorageState } from '~/modules/browser/storage';
import { useCartCtx } from '../context';
import { DateTimeSDK } from '@funkey/shared';
import { useModalState } from '~/components/primitives/Modal';
import { useQueryState } from 'nuqs';
import { Wishlist_SharedPayload } from './wishlist.shared';
import { Skeleton } from '~/components/primitives/skeleton';
import { useStateSet } from '~/utils/react';
import { pipe } from 'effect';
import { PORTAL_RELATIVE_HREF_CART } from '../hooks';
import { addressCityLabel } from '~/features/activities/addressCityLabel';
import { useCustomLocationLabel } from '~/features/activities/useCustomLocationLabel';
import { LOCATION_CUSTOM_VALUE } from '../cartItemLocation';
import { partnerComesToYou } from '~/features/activities/pdp/location-widget/utils/partnerComesToYou';
import { partnerHasFixedVenue } from '~/features/activities/pdp/location-widget/utils/partnerHasFixedVenue';

export const __Wishlist_Page_Content: React.FC<{}> = _props => {
    const i18n = useI18n();
    const portal = usePortalCtx();
    const portal_HREFs = usePortalHREFs();
    const wishlist = useWishlistState();
    const posthog = usePostHog();

    const trpc = useTRPC();
    const cartID = useCartCtx().id;
    const cartQuery = useQuery(trpc.cart.get.queryOptions(undefined, { enabled: !!cartID }));
    const cartVariantIds = useMemo(
        () =>
            new Set(
                (cartQuery.data?.items ?? [])
                    .map(item => item.activityVariantId)
                    .filter((id): id is number => id !== null),
            ),
        [cartQuery.data?.items],
    );

    const dismissedInCartIds = useStateSet<number>([]);

    const participants = useLocalStorageState(CACHED_PARTICIPANTS_FILTER);
    const hasMounted = useHasMounted();

    const openShareWishlistDialog = useWishlist_ShareLinkDialog_OpenAction();

    const IMPORT_SHARED_WISHLIST_DIALOG = (
        <Wishlist_SharedItemsDialog
            hasExisting={wishlist.items.length > 0}
            onImport={(mode, payload) => {
                if (payload.participants) participants.setState(payload.participants);
                if (mode === 'replace') wishlist.clear();

                for (const item of payload.items) {
                    wishlist.upsert({
                        ...item,
                        participants: payload.participants,
                    });
                }
                posthog.capture('wishlist.redeemed', {
                    portal: portal.portal,
                    item_count: payload.items.length,
                    mode,
                });
            }}
        />
    );

    const wishlistItems = useMemo(
        () => ({
            ...wishlist.items.reduce(
                (acc, next) => {
                    acc[
                        portal.portal === 'BIZZ' &&
                        portal.bizz.excludedActivityIDs.includes(next.activityId)
                            ? 'unavailable'
                            : 'available'
                    ].push(next);

                    return acc;
                },
                {
                    available: new Array<WishlistItem>(),
                    unavailable: new Array<WishlistItem>(),
                },
            ),
            all: wishlist.items,
        }),
        [wishlist.items, portal],
    );

    if (!wishlistItems.all.length) {
        return (
            <div
                data-testid="wishlist-empty"
                className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-blue-200 bg-blue-50/50 py-12 text-center"
            >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                    <Icon name="heart" size="lg" className="text-blue-400" />
                </div>
                <div>
                    <p className="text-lg font-bold text-primary">
                        {i18n._('wishlist.empty.title', undefined, {
                            message: 'Your wishlist is empty',
                        })}
                    </p>
                    <p className="mt-1 text-sm text-blue-600">
                        {i18n._('wishlist.empty.description', undefined, {
                            message:
                                'Save activities with the heart icon and convert them to your quote cart later.',
                        })}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <ButtonLink
                        data-testid="wishlist-empty-browse"
                        variant="tertiary"
                        size="sm"
                        iconBefore="activity-selection"
                        href={portal_HREFs.PLP()}
                    >
                        {i18n._('Browse activities')}
                    </ButtonLink>
                    <Wishlist_Empty.GoToCartCTA
                        hasMounted={hasMounted}
                        isLoading={cartQuery.isLoading}
                        hasItems={!!cartQuery.data?.items.length}
                    />
                </div>

                {IMPORT_SHARED_WISHLIST_DIALOG}
            </div>
        );
    }

    return (
        <div data-testid="wishlist-panel">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <NumberInput
                    value={participants.state}
                    onChange={value => participants.setState(value ?? 0)}
                    min={1}
                    max={MAX_PARTICIPANTS}
                    integerOnly
                    compact
                    suffix={i18n._('participants')}
                    className="w-48! flex-none!"
                />

                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <Button
                        data-testid="wishlist-share"
                        variant="outline"
                        iconBefore="share"
                        size="sm"
                        onClick={openShareWishlistDialog}
                    >
                        {i18n._('wishlist.share', undefined, { message: 'Share wishlist' })}
                    </Button>
                </div>
            </div>

            {pipe(
                wishlistItems.available.filter(
                    item =>
                        !(
                            item.variantId !== null &&
                            cartVariantIds.has(item.variantId) &&
                            !dismissedInCartIds.has(item.activityId)
                        ),
                ),
                items =>
                    items.length > 0 && (
                        <ul className="space-y-4">
                            {items.map(item => (
                                <Wishlist_Item.Card
                                    key={item.activityId}
                                    item={item}
                                    participants={participants.state}
                                    onDismissInCart={() => dismissedInCartIds.add(item.activityId)}
                                />
                            ))}
                        </ul>
                    ),
            )}

            {pipe(
                wishlistItems.all.filter(
                    item =>
                        item.variantId !== null &&
                        cartVariantIds.has(item.variantId) &&
                        !dismissedInCartIds.has(item.activityId),
                ),
                items =>
                    items.length === 0 ? null : (
                        <>
                            <div
                                className="mb-4 mt-8 flex items-center gap-2 pt-6"
                                data-testid="wishlist-already-in-cart-section"
                            >
                                <Icon
                                    name="exclamation-circle"
                                    size="sm"
                                    className="text-orange-500"
                                />
                                <p className="text-sm font-bold text-primary">
                                    {i18n._('wishlist.already-in-cart-section.title', undefined, {
                                        message: 'Already in your cart',
                                    })}
                                </p>
                            </div>
                            <ul className="space-y-4">
                                {items.map(item => (
                                    <Wishlist_Item.Card
                                        key={item.activityId}
                                        item={item}
                                        participants={participants.state}
                                        onDismissInCart={() =>
                                            dismissedInCartIds.add(item.activityId)
                                        }
                                        alreadyInCart
                                    />
                                ))}
                            </ul>
                        </>
                    ),
            )}

            {wishlistItems.unavailable.length > 0 && (
                <>
                    <div
                        className="mb-4 mt-8 flex items-center gap-2 pt-6"
                        data-testid="wishlist-unavailable-section"
                    >
                        <Icon name="info-circle" size="sm" className="text-orange-500" />
                        <p className="text-sm font-bold text-primary">
                            {i18n._(
                                'wishlist.unavailable-section.title_{bizzName}',
                                { bizzName: portal.portal === 'BIZZ' ? portal.bizz.name : '' },
                                { message: 'Not available for {bizzName}' },
                            )}
                        </p>
                    </div>
                    <ul className="space-y-4">
                        {wishlistItems.unavailable.map(item => (
                            <Wishlist_Item.Card
                                key={item.activityId}
                                item={item}
                                participants={participants.state}
                                onDismissInCart={() => dismissedInCartIds.add(item.activityId)}
                            />
                        ))}
                    </ul>
                </>
            )}

            {IMPORT_SHARED_WISHLIST_DIALOG}

            <Wishlist_ShareLinkDialog_Mount participants={participants.state} />
        </div>
    );
};

namespace Wishlist_Empty {
    export const GoToCartCTA: React.FC<{
        hasMounted: boolean;
        isLoading: boolean;
        hasItems: boolean;
    }> = props => {
        const i18n = useI18n();
        const cartHREF = usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART);

        if (!props.hasMounted || props.isLoading) {
            return <Skeleton aria-hidden className="h-9 w-32 rounded-full" />;
        }

        if (props.hasItems) return null;

        return (
            <ButtonLink
                data-testid="wishlist-empty-go-to-cart"
                variant="outline"
                size="sm"
                iconBefore="bag"
                href={cartHREF}
            >
                {i18n._('wishlist.empty.go-to-cart', undefined, {
                    message: 'Go to cart',
                })}
            </ButtonLink>
        );
    };
}

function useHasMounted() {
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    return hasMounted;
}

namespace Wishlist_Item {
    export const Card: React.FC<{
        item: WishlistItem;
        participants?: number;
        onDismissInCart(): void;
        /** When true, this item's variant is already in the cart (shown in the "already in cart" section) */
        alreadyInCart?: boolean;
    }> = props => {
        const i18n = useI18n();
        const hrefs = usePortalHREFs();
        const portal = usePortalCtx();
        const posthog = usePostHog();
        const wishlist = useWishlistState();
        const cart = useCartState();
        const addToCart = cart.useUpsertCartItemMutation();

        const [isAdding, setIsAdding] = useState(false);
        const AddToCartDialog_state = useModalState(false);

        const query = useWishlist_Item_ActivityQuery(props.item.activityId);

        const isDeleted = !query.isLoading && query.data !== undefined && !query.data;
        const title = query.data?.name || props.item.name || `#${props.item.activityId}`;
        const slug = query.data?.group?.slug || props.item.slug;
        const image = query.data?.media?.header?.path || props.item.imagePath;
        const priceType = query.data?.priceType ?? 'PER_PARTICIPANT';

        const href =
            slug && !isDeleted
                ? `${hrefs.PDP({ slug, id: props.item.activityId, participants: props.participants })}&nomodal`
                : null;

        const singleVariant = query.data?.variants.length === 1 ? query.data.variants[0]! : null;
        const singleLanguage = query.data?.languages.length === 1 ? query.data.languages[0]! : null;

        // Location is pickable straight from the wishlist row, mirroring the cart, so the
        // choice is made (and shared) before the item ever moves to the cart.
        const comesToYou = partnerComesToYou({
            transportMultiplier: query.data?.transportMultiplier,
            transportCostTiers: query.data?.transportCosts,
            hasCustomLocations: query.data?.hasCustomLocations,
            addresses: query.data?.activityAddresses,
            hasOwnLocations: query.data?.hasOwnLocations,
            hasFixedLocations: query.data?.hasFixedLocations,
        });
        const activityAddresses = partnerHasFixedVenue({
            addresses: query.data?.activityAddresses,
            hasOwnLocations: query.data?.hasOwnLocations,
            hasFixedLocations: query.data?.hasFixedLocations,
        })
            ? (query.data?.activityAddresses ?? [])
            : [];
        const canPickLocation = comesToYou || activityAddresses.length > 1;
        const hasLocationOptions = comesToYou || activityAddresses.length > 0;
        const customLocationLabel = useCustomLocationLabel();
        const currentLocationValue =
            props.item.location === LOCATION_CUSTOM_VALUE
                ? LOCATION_CUSTOM_VALUE
                : props.item.location != null
                  ? String(props.item.location)
                  : '';

        useEffect(() => {
            if (singleVariant && props.item.variantId === null) {
                wishlist.updateSelection({
                    activityId: props.item.activityId,
                    variantId: Number(singleVariant.id),
                });
            }
        }, [singleVariant, props.item.variantId, props.item.activityId]);

        useEffect(() => {
            if (props.item.languageId !== null) return;
            if (singleLanguage) {
                wishlist.updateSelection({
                    activityId: props.item.activityId,
                    languageId: Number(singleLanguage.id),
                });
            } else if (props.item.addedLanguage && !!query.data?.languages.length) {
                const match = query.data.languages.find(
                    l =>
                        l.code &&
                        typeof l.code === 'string' &&
                        l.code.toLowerCase() === props.item.addedLanguage!.toLowerCase(),
                );
                if (match) {
                    wishlist.updateSelection({
                        activityId: props.item.activityId,
                        languageId: Number(match.id),
                    });
                }
            }
        }, [
            singleLanguage,
            props.item.languageId,
            props.item.activityId,
            props.item.addedLanguage,
            query.data?.languages,
        ]);

        const variantOptionsResult = useVariantOptions({
            variants: query.data?.variants ?? [],
            participants: props.participants,
            priceType,
        });
        const variantOptions = variantOptionsResult.variantOptions;
        const shouldDisplayPrices = variantOptionsResult.shouldDisplayPrices;
        const selectedVariant = query.data?.variants.find(
            v => String(v.id) === String(props.item.variantId),
        );
        const priceInfo = usePriceInfo({
            selectedVariant,
            participants: props.participants,
            priceType,
            shouldDisplayPrices,
        });
        const isUnavailableForBizz = useMemo(() => {
            if (portal.portal !== 'BIZZ') return null;
            if (!portal.bizz.excludedActivityIDs.includes(props.item.activityId)) {
                return null;
            }

            return portal.bizz.name;
        }, [portal, props.item.activityId]);

        async function commitMoveToCart(
            data: { variantId: number; languageId: number },
            source: 'dialog' | 'inline',
        ) {
            setIsAdding(true);
            try {
                await addToCart.mutateAsync({
                    cartItemID: null,
                    variantId: data.variantId,
                    languageId: data.languageId,
                    boostAdId: null,
                    location: props.item.location ?? null,
                });
                wishlist.remove(props.item.activityId);
                posthog.capture('wishlist.item.moved_to_cart', {
                    portal: portal.portal,
                    activity_id: props.item.activityId,
                    activity_name: props.item.name,
                    source,
                    wishlist_duration_seconds: Math.round(
                        (Date.now() - new Date(props.item.addedAt).getTime()) / 1000,
                    ),
                });
            } finally {
                setIsAdding(false);
            }
        }

        return (
            <>
                <ActivityItemCard
                    listItemProps={{
                        'data-testid': 'wishlist-item-card',
                        'data-activity-id': String(props.item.activityId),
                    }}
                    className={
                        isDeleted ? 'border-red-200 bg-red-50/50' : 'border-blue-100 bg-white'
                    }
                    imageSrc={image}
                    imageAlt={title}
                    imageHref={href}
                    imageClassName="sm:w-32"
                    imageImageClassName={isDeleted ? 'opacity-50 grayscale' : undefined}
                    title={
                        <Tooltip
                            content={i18n._(
                                'wishlist.item.added-on_{date}',
                                {
                                    date: new Intl.DateTimeFormat(i18n.locale, {
                                        timeZone: DateTimeSDK.TIME_ZONE,
                                        dateStyle: 'medium',
                                        timeStyle: 'short',
                                    }).format(new Date(props.item.addedAt)),
                                },
                                { message: 'Added on {date}' },
                            )}
                        >
                            <ActivityListCardTitle
                                href={href}
                                className={
                                    isDeleted
                                        ? 'text-sm leading-tight text-red-500 line-through hover:text-red-500 sm:text-base'
                                        : 'text-sm leading-tight sm:text-base'
                                }
                            >
                                {title}
                            </ActivityListCardTitle>
                        </Tooltip>
                    }
                    topRightAction={
                        isUnavailableForBizz ? (
                            <Tooltip
                                content={i18n._(
                                    'wishlist.item.unavailable-for-bizz_{bizzName}',
                                    { bizzName: isUnavailableForBizz },
                                    { message: 'This activity is not available for {bizzName}' },
                                )}
                            >
                                <Badge variant="destructive">
                                    {i18n._('wishlist.item.not-available', undefined, {
                                        message: 'Not available',
                                    })}
                                </Badge>
                            </Tooltip>
                        ) : (
                            <button
                                type="button"
                                data-testid="wishlist-item-remove"
                                onClick={() => wishlist.remove(props.item.activityId)}
                                className="shrink-0 rounded-full p-1.5 text-blue-400 transition-colors hover:bg-blue-50 hover:text-orange-500 sm:p-2"
                                aria-label={i18n._('wishlist.item.remove', undefined, {
                                    message: 'Remove',
                                })}
                            >
                                <Icon name="trash" size="sm" />
                            </button>
                        )
                    }
                >
                    {isDeleted && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs font-medium text-red-500">
                            <Icon name="info-circle" size="xs" />
                            {i18n._('wishlist.item.deleted', undefined, {
                                message: 'This activity is no longer available',
                            })}
                        </div>
                    )}

                    {!isDeleted && (
                        <div className="mt-1.5 flex w-full flex-col gap-2.5 sm:mt-2 sm:gap-3">
                            <VariantLanguageInlineSelect
                                id={props.item.activityId.toString()}
                                isLoading={query.isLoading}
                                variants={query.data?.variants ?? []}
                                singleVariant={singleVariant}
                                singleVariantTooltip={i18n._('wishlist.single-variant', undefined, {
                                    message: 'This activity has only one variant',
                                })}
                                variantValue={props.item.variantId?.toString() ?? undefined}
                                variantOptions={variantOptions}
                                onVariantChange={value =>
                                    wishlist.updateSelection({
                                        activityId: props.item.activityId,
                                        variantId: Number(value),
                                    })
                                }
                                languages={query.data?.languages ?? []}
                                singleLanguage={singleLanguage}
                                singleLanguageTooltip={i18n._(
                                    'wishlist.single-language',
                                    undefined,
                                    {
                                        message: 'This activity has only one language',
                                    },
                                )}
                                languageValue={props.item.languageId?.toString() ?? undefined}
                                onLanguageChange={value =>
                                    wishlist.updateSelection({
                                        activityId: props.item.activityId,
                                        languageId: Number(value),
                                    })
                                }
                                className="min-w-0"
                                locationSlot={
                                    hasLocationOptions ? (
                                        <InputSelect
                                            id={`wishlist-${props.item.activityId}-location`}
                                            compact
                                            variant="minimal"
                                            className="min-w-0 max-w-full"
                                            data-testid="wishlist-item-location"
                                            // Single fixed venue + no transport → nothing to choose.
                                            disabled={!canPickLocation}
                                            placeholder={i18n._(
                                                'pdp.location.dropdown.placeholder',
                                                undefined,
                                                { message: 'Choose your location' },
                                            )}
                                            value={
                                                !canPickLocation && currentLocationValue === ''
                                                    ? String(activityAddresses[0]?.id ?? '')
                                                    : currentLocationValue
                                            }
                                            options={[
                                                ...activityAddresses.map((address, index) => ({
                                                    value: String(address.id),
                                                    label:
                                                        addressCityLabel(address.name) ??
                                                        i18n._(
                                                            'pdp.location.dropdown.location_n',
                                                            { n: index + 1 },
                                                            { message: 'Location {n}' },
                                                        ),
                                                })),
                                                ...(comesToYou
                                                    ? [
                                                          {
                                                              value: LOCATION_CUSTOM_VALUE,
                                                              label: customLocationLabel,
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                            onChange={next =>
                                                wishlist.updateSelection({
                                                    activityId: props.item.activityId,
                                                    location:
                                                        next === LOCATION_CUSTOM_VALUE
                                                            ? LOCATION_CUSTOM_VALUE
                                                            : next === ''
                                                              ? null
                                                              : Number(next),
                                                })
                                            }
                                        />
                                    ) : null
                                }
                            />
                            {shouldDisplayPrices && selectedVariant && priceInfo && (
                                <p
                                    data-testid="wishlist-item-price"
                                    className="ml-1 text-sm text-primary sm:ml-0"
                                >
                                    <span className="font-bold">
                                        {Units.formatEUR(priceInfo.total * 100, i18n.locale)}
                                    </span>
                                    <span className="ml-1 font-normal text-blue-500">
                                        {i18n._(
                                            'price.total-for-participants_{participants}',
                                            { participants: props.participants },
                                            {
                                                message: 'for {participants} pers.',
                                            },
                                        )}
                                        {' · '}
                                        <VATKindDisplay hideParentheses />
                                    </span>
                                </p>
                            )}
                            {isUnavailableForBizz ? (
                                <Button
                                    data-testid="wishlist-item-remove-outline"
                                    className="w-full shrink-0 sm:absolute sm:bottom-3 sm:right-3 sm:w-auto"
                                    size="sm"
                                    variant="outline"
                                    iconBefore="trash"
                                    onClick={() => wishlist.remove(props.item.activityId)}
                                >
                                    {i18n._('wishlist.remove-from-wishlist', undefined, {
                                        message: 'Remove from wishlist',
                                    })}
                                </Button>
                            ) : props.alreadyInCart ? (
                                <div className="flex w-full gap-2 sm:absolute sm:bottom-3 sm:right-3 sm:w-auto">
                                    <Button
                                        data-testid="wishlist-item-remove-duplicate"
                                        className="w-full sm:w-auto"
                                        size="sm"
                                        variant="secondary"
                                        iconBefore="trash"
                                        onClick={() => wishlist.remove(props.item.activityId)}
                                    >
                                        {i18n._('wishlist.remove-from-wishlist', undefined, {
                                            message: 'Remove from wishlist',
                                        })}
                                    </Button>
                                    <Button
                                        data-testid="wishlist-keep-duplicate-in-cart"
                                        size="sm"
                                        variant="outline"
                                        onClick={props.onDismissInCart}
                                    >
                                        {i18n._('wishlist.keep', undefined, {
                                            message: 'Keep',
                                        })}
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    data-testid="wishlist-add-to-cart"
                                    className="w-full shrink-0 sm:absolute sm:bottom-3 sm:right-3 sm:w-auto"
                                    size="sm"
                                    variant="outline"
                                    iconBefore="plus"
                                    isLoading={isAdding}
                                    disabled={isAdding}
                                    onClick={async () => {
                                        if (
                                            props.item.variantId === null ||
                                            props.item.languageId === null
                                        ) {
                                            return void AddToCartDialog_state.open();
                                        }
                                        if (isAdding) return;
                                        await commitMoveToCart(
                                            {
                                                languageId: props.item.languageId,
                                                variantId: props.item.variantId,
                                            },
                                            'inline',
                                        );
                                    }}
                                >
                                    {i18n._('wishlist.add-to-cart-direct', undefined, {
                                        message: 'Add to cart',
                                    })}
                                </Button>
                            )}
                        </div>
                    )}
                </ActivityItemCard>

                {AddToCartDialog_state.isOpen && !!query.data && (
                    <AddToCartDialog
                        item={props.item}
                        activity={query.data}
                        participants={props.participants}
                        onSubmit={async data => {
                            if (isAdding) return;
                            await commitMoveToCart(data, 'dialog');

                            AddToCartDialog_state.close();
                        }}
                        onClose={AddToCartDialog_state.close}
                        isPending={isAdding}
                    />
                )}
            </>
        );
    };

    const AddToCartDialog: React.FC<{
        item: WishlistItem;
        activity: useWishlist_Item_ActivityQuery.Data;
        participants?: number;
        onSubmit(data: { languageId: number; variantId: number }): void;
        onClose(): void;
        isPending: boolean;
    }> = props => {
        const i18n = useI18n();
        const wishlist = useWishlistState();

        const comesToYou = partnerComesToYou({
            transportMultiplier: props.activity.transportMultiplier,
            transportCostTiers: props.activity.transportCosts,
            hasCustomLocations: props.activity.hasCustomLocations,
            addresses: props.activity.activityAddresses,
            hasOwnLocations: props.activity.hasOwnLocations,
            hasFixedLocations: props.activity.hasFixedLocations,
        });
        const dialogCustomLocationLabel = useCustomLocationLabel();

        const [locationValue, setLocationValue] = useState<string>(() => {
            if (props.item.location === LOCATION_CUSTOM_VALUE) return LOCATION_CUSTOM_VALUE;
            return props.item.location != null ? String(props.item.location) : '';
        });

        const [variantId, setVariantId] = useState<string | null>(() => {
            if (props.item.variantId) return props.item.variantId.toString();

            if (props.activity.variants.length === 1) {
                return props.activity.variants[0]!.id;
            }

            return null;
        });
        const [languageId, setLanguageId] = useState<string | null>(() => {
            if (props.item.variantId) return props.item.variantId.toString();

            if (props.activity.languages.length === 1) {
                return props.activity.languages[0]!.id;
            }

            return null;
        });

        const variantOptionsResult = useVariantOptions({
            variants: useMemo(
                () => props.activity.variants.map(x => ({ ...x, id: x.id.toString() })),
                [props.activity.variants],
            ),
            participants: props.participants,
            priceType: props.activity.priceType,
        });

        // These two used to sit in an IIFE behind `shouldDisplayPrices`, so they stopped
        // running whenever prices were hidden and the hook count changed between renders.
        const selectedVariant = useMemo(
            () => props.activity.variants.find(x => x.id === variantId),
            [props.activity.variants, variantId],
        );
        const priceInfo = usePriceInfo({
            selectedVariant: selectedVariant ?? undefined,
            participants: props.participants,
            priceType: props.activity.priceType,
            shouldDisplayPrices: variantOptionsResult.shouldDisplayPrices,
        });

        const canSubmit = !!variantId && !!languageId;

        return (
            <Dialog
                open
                onOpenChange={open => {
                    if (!open) props.onClose();
                }}
            >
                <DialogContent className="sm:max-w-md" data-testid="wishlist-variant-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {i18n._('wishlist.dialog.title', undefined, {
                                message: 'Select variant & language',
                            })}
                        </DialogTitle>
                        <DialogDescription>{props.item.name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {props.activity.variants.length > 1 && (
                            <div data-testid="wishlist-dialog-variant">
                                <InputSelect
                                    id="dialog-variant"
                                    placeholder={i18n._('Select variant')}
                                    value={variantId}
                                    options={variantOptionsResult.variantOptions}
                                    onChange={setVariantId}
                                />
                            </div>
                        )}
                        <div data-testid="wishlist-dialog-language">
                            <InputSelect
                                id="dialog-language"
                                placeholder={i18n._('Select language')}
                                options={props.activity.languages}
                                labelField="name"
                                valueField="id"
                                value={languageId}
                                onChange={setLanguageId}
                            />
                        </div>
                        {(props.activity.activityAddresses.length > 0 || comesToYou) && (
                            <div data-testid="wishlist-dialog-location">
                                <InputSelect
                                    id="dialog-location"
                                    placeholder={i18n._(
                                        'pdp.location.dropdown.placeholder',
                                        undefined,
                                        { message: 'Choose your location' },
                                    )}
                                    value={locationValue}
                                    options={[
                                        ...props.activity.activityAddresses.map(
                                            (address, index) => ({
                                                value: String(address.id),
                                                label:
                                                    addressCityLabel(address.name) ??
                                                    i18n._(
                                                        'pdp.location.dropdown.location_n',
                                                        { n: index + 1 },
                                                        { message: 'Location {n}' },
                                                    ),
                                            }),
                                        ),
                                        ...(comesToYou
                                            ? [
                                                  {
                                                      value: LOCATION_CUSTOM_VALUE,
                                                      label: dialogCustomLocationLabel,
                                                  },
                                              ]
                                            : []),
                                    ]}
                                    onChange={next => {
                                        setLocationValue(next);
                                        wishlist.updateSelection({
                                            activityId: props.item.activityId,
                                            location:
                                                next === LOCATION_CUSTOM_VALUE
                                                    ? LOCATION_CUSTOM_VALUE
                                                    : Number(next),
                                        });
                                    }}
                                />
                            </div>
                        )}
                        {variantOptionsResult.shouldDisplayPrices &&
                            selectedVariant &&
                            priceInfo && (
                                <div
                                    data-testid="wishlist-dialog-price"
                                    className="rounded-lg border border-blue-100 bg-blue-50/50 p-3"
                                >
                                    <p className="text-sm font-bold text-primary">
                                        {Units.formatEUR(priceInfo.total * 100, i18n.locale)}
                                    </p>
                                    <p className="text-xs text-blue-500">
                                        {i18n._(
                                            'wishlist.dialog.price-info_{participants}',
                                            { participants: props.participants },
                                            {
                                                message:
                                                    'Total price for {participants} participants',
                                            },
                                        )}
                                        {' · '}
                                        <VATKindDisplay hideParentheses />
                                    </p>
                                </div>
                            )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={props.onClose}>
                            {i18n._('Cancel')}
                        </Button>
                        <Button
                            data-testid="wishlist-dialog-add-to-cart"
                            variant="tertiary"
                            iconBefore="plus"
                            disabled={!canSubmit || props.isPending}
                            isLoading={props.isPending}
                            onClick={async () => {
                                if (variantId === null || languageId === null) return;

                                props.onSubmit({
                                    languageId: Number(languageId),
                                    variantId: Number(variantId),
                                });
                            }}
                        >
                            {i18n._('wishlist.add-to-cart-direct', undefined, {
                                message: 'Add to cart',
                            })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    };

    declare namespace useWishlist_Item_ActivityQuery {
        export type Data = NonNullable<
            ReturnType<typeof __Wishlist_Item_useActivityQuery_transformData>
        >;
    }
    function useWishlist_Item_ActivityQuery(activityID: number) {
        const query = api.useGraphqlQuery(WISHLIST_ACTIVITY_QUERY, {
            activityID: activityID,
        });

        return useMemo(() => {
            return {
                ...query,
                data: __Wishlist_Item_useActivityQuery_transformData(query.data),
            };
        }, [query]);
    }

    function __Wishlist_Item_useActivityQuery_transformData(
        data: api.GraphQLDocument.output<typeof WISHLIST_ACTIVITY_QUERY> | undefined,
    ) {
        if (!data?.activity) return null;

        const { activityVariants, activityLanguages, priceType, ...activity } = data.activity;

        return Object.assign(activity, {
            variants: activityVariants ?? [],
            languages: activityLanguages ?? [],
            priceType: priceType ?? 'PER_PARTICIPANT',
        });
    }

    const WISHLIST_ACTIVITY_QUERY = graphql(`
        query Wishlist_Item_CardData($activityID: Int!) {
            activity(id: $activityID) {
                id
                name
                priceType
                group {
                    slug
                }
                media {
                    header {
                        path
                    }
                }
                activityVariants {
                    id
                    name
                    duration
                    startupCost
                    extraCost
                    extraCostPerCount
                    priceDescription
                    priceRules {
                        price
                        participants
                        extraPrice
                    }
                }
                activityLanguages {
                    id
                    name
                    code
                }
                activityAddresses {
                    id
                    name
                }
                transportMultiplier
                transportCosts {
                    distance
                    extraCost
                    costPerKm
                }
                hasOwnLocations
                hasFixedLocations
                hasCustomLocations
            }
        }
    `);
}

const Wishlist_SharedItemsDialog: React.FC<{
    hasExisting: boolean;
    onImport(
        mode: 'replace' | 'add-all',
        payload: Wishlist_SharedPayload.Data,
    ): void | Promise<unknown>;
}> = props => {
    const i18n = useI18n();

    const [payload, setPayload] = useQueryState(
        Wishlist_SharedPayload.QUERY_PARAM_NAME,
        Wishlist_SharedPayload.QUERY_PARAM_PARSER.withOptions({
            shallow: true,
            scroll: false,
            history: 'replace',
            limitUrlUpdates: {
                method: 'debounce',
                timeMs: 1000,
            },
        }),
    );

    if (payload === null) return null;

    const onSubmit: typeof props.onImport = async (...args) => {
        await props.onImport(...args);
        setPayload(null);
    };

    return (
        <Dialog
            open
            onOpenChange={open => {
                if (!open) setPayload(null);
            }}
        >
            <DialogContent
                className="sm:max-w-md"
                data-testid="shared-wishlist-dialog"
                onOpenAutoFocus={e => {
                    const input = (
                        e.currentTarget as HTMLElement | null
                    )?.querySelector<HTMLInputElement>('input[type="number"]');
                    if (input) {
                        e.preventDefault();
                        input.focus();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>
                        {i18n._('wishlist.import.title', undefined, {
                            message: 'Shared wishlist received',
                        })}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n._(
                            'wishlist.import.description_{count}',
                            { count: payload.items.length },
                            {
                                message:
                                    'Someone shared a wishlist with {count} activities. What would you like to do?',
                            },
                        )}
                    </DialogDescription>
                </DialogHeader>
                <ul className="max-h-48 overflow-y-auto rounded-lg border border-blue-100 bg-blue-50/50">
                    {payload.items.map(item => (
                        <li key={item.activityId} className="flex items-center gap-3 px-3 py-2">
                            {item.imagePath ? (
                                <img
                                    src={item.imagePath}
                                    alt={item.name}
                                    className="h-10 w-14 shrink-0 rounded object-cover"
                                />
                            ) : (
                                <div className="h-10 w-14 shrink-0 rounded bg-blue-100" />
                            )}
                            <span className="truncate text-sm font-medium text-primary">
                                {item.name}
                            </span>
                        </li>
                    ))}
                </ul>
                <div className="flex flex-col gap-2 py-2">
                    <NumberInput
                        value={payload.participants ?? undefined}
                        onChange={participants =>
                            setPayload(prev =>
                                prev === null
                                    ? null // Will never be hit
                                    : {
                                          ...prev,
                                          participants: participants ?? null,
                                      },
                            )
                        }
                        min={1}
                        max={MAX_PARTICIPANTS}
                        integerOnly
                        compact
                        suffix={i18n._('participants')}
                        className="w-48! flex-none!"
                    />
                    {props.hasExisting && (
                        <Button
                            variant="tertiary"
                            onClick={() => onSubmit('replace', payload)}
                            data-testid="shared-wishlist-replace"
                        >
                            {i18n._('wishlist.import.replace', undefined, {
                                message: 'Replace my wishlist',
                            })}
                        </Button>
                    )}
                    <Button
                        variant={props.hasExisting ? 'outline' : 'tertiary'}
                        onClick={() => onSubmit('add-all', payload)}
                        data-testid="shared-wishlist-add-all"
                    >
                        {i18n._('wishlist.import.add-to-wishlist', undefined, {
                            message: 'Add to my wishlist',
                        })}
                    </Button>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setPayload(null)}
                        data-testid="shared-wishlist-cancel"
                    >
                        {i18n._('Cancel')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const _Wishlist_Aside_GoToCartButton: React.FC = _props => {
    const cart = useCartState().useCartQuery();
    const router = usePortalRouter();
    const i18n = useI18n();

    return (
        !!cart.data?.items.length && (
            <ButtonLink
                href={router.relativePath(`/cart`)}
                variant="tertiary"
                iconBefore="bag"
                size="sm"
                className="mt-6 w-full"
            >
                {i18n._('wishlist.sidebar.go-to-cart', undefined, {
                    message: 'Go to cart',
                })}
            </ButtonLink>
        )
    );
};
